import React from "react";
import { useTranslation } from "react-i18next";
import styles from "./FilterBars.module.css";
import MultiSelect from "./MultiSelect";
import { SelectOption } from "../types";

interface CompanyFilter {
    placeholder: string;
    options: SelectOption[];
    selectedIds: string[];
    onSelectedIdsChange: (next: string[]) => void;
    loading?: boolean;
}

interface ProductFilterBarProps {
    selectorPlaceholder: string;
    options: SelectOption[];
    selectedIds: string[];
    onSelectedIdsChange: (next: string[]) => void;
    optionsLoading?: boolean;
    /** Owner-only company multiselect shown left of the product selector. */
    companyFilter?: CompanyFilter | null;
    canApply: boolean;
    applying: boolean;
    onApply: () => void;
    onReset: () => void;
}

/**
 * Product/company-only filter bar for the owner layout of the Mobile-app tab.
 * Its Apply only refreshes the visitor/track KPIs below — it reuses the period
 * already applied via DateFilterBar, so it does not own any date state.
 */
const ProductFilterBar: React.FC<ProductFilterBarProps> = ({
    selectorPlaceholder,
    options,
    selectedIds,
    onSelectedIdsChange,
    optionsLoading,
    companyFilter,
    canApply,
    applying,
    onApply,
    onReset,
}) => {
    const { t } = useTranslation();
    const tt = t as unknown as (k: string) => string;

    return (
        <section className={styles.card} aria-label={tt("analyticsProductFilters")}>
            <div className={styles.header}>
                <h3 className={styles.title}>{tt("analyticsProductFilters")}</h3>
                <p className={styles.hint}>{tt("multipleSelectionAvailable")}</p>
            </div>
            <div className={companyFilter ? styles.selectorPair : styles.mainOnly}>
                {companyFilter ? (
                    <MultiSelect
                        placeholder={companyFilter.placeholder}
                        options={companyFilter.options}
                        value={companyFilter.selectedIds}
                        onChange={companyFilter.onSelectedIdsChange}
                        loading={companyFilter.loading}
                    />
                ) : null}
                <MultiSelect
                    placeholder={selectorPlaceholder}
                    options={options}
                    value={selectedIds}
                    onChange={onSelectedIdsChange}
                    loading={optionsLoading}
                />
            </div>
            <div className={styles.actions}>
                <button type="button" className={styles.applyButton} disabled={!canApply || applying} onClick={onApply}>
                    {applying ? tt("loading") : tt("apply")}
                </button>
                <button type="button" className={styles.resetButton} onClick={onReset}>
                    {tt("reset")}
                </button>
            </div>
        </section>
    );
};

export default ProductFilterBar;
